import React, { useEffect, useState } from "react";
import { api, formatGHS, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", brand: "", category: "sedan", daily_rate: 500, daily_rate_outside: null, transmission: "automatic", seats: 5,
  fuel: "petrol", luggage: 2, image_url: "", gallery: [], features: [], description: "", available: true,
};

const CATS = ["economy", "sedan", "compact_suv", "full_suv", "van", "luxury"];

export default function AdminCarsPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // null | "new" | car object

  const load = () => {
    setLoading(true);
    api.get("/cars?available_only=false").then((r) => setCars(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const del = async (car) => {
    if (!window.confirm(`Delete ${car.brand} ${car.name}?`)) return;
    try {
      await api.delete(`/admin/cars/${car.id}`);
      toast.success("Car deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8" data-testid="admin-cars-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="eyebrow">Fleet</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Manage cars</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpenForm("new")} data-testid="admin-add-car-btn">
          <Plus size={14} /> Add car
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-cars-table">
            <thead className="bg-[#0d2040] text-white">
              <tr>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Car</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Category</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Rate/day</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Trans</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Seats</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Status</th>
                <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cars.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-[#f7f8fa]">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={c.image_url} alt={c.name} className="w-14 h-10 object-cover" />
                      <span className="font-medium text-[#0d2040]">{c.brand} {c.name}</span>
                    </div>
                  </td>
                  <td className="p-3 capitalize">{c.category}</td>
                  <td className="p-3 font-display font-bold text-[#e63946]">{formatGHS(c.daily_rate)}</td>
                  <td className="p-3 capitalize">{c.transmission}</td>
                  <td className="p-3">{c.seats}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${c.available ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {c.available ? "Available" : "Unavailable"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button className="p-2 hover:bg-gray-100" onClick={() => setOpenForm(c)} data-testid={`admin-edit-car-${c.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button className="p-2 hover:bg-red-50 text-[#e63946]" onClick={() => del(c)} data-testid={`admin-delete-car-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && <CarFormModal car={openForm === "new" ? null : openForm} onClose={() => setOpenForm(null)} onSaved={() => { setOpenForm(null); load(); }} />}
    </div>
  );
}

function CarFormModal({ car, onClose, onSaved }) {
  const [form, setForm] = useState(car || EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        daily_rate: parseFloat(form.daily_rate),
        daily_rate_outside: form.daily_rate_outside ? parseFloat(form.daily_rate_outside) : null,
        seats: parseInt(form.seats),
        luggage: parseInt(form.luggage),
        features: Array.isArray(form.features) ? form.features : String(form.features).split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (car) {
        await api.put(`/admin/cars/${car.id}`, payload);
        toast.success("Car updated");
      } else {
        await api.post("/admin/cars", payload);
        toast.success("Car added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-car-form-modal">
      <div className="bg-white max-w-2xl w-full my-10">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-[#0d2040]">{car ? "Edit car" : "Add new car"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100" data-testid="admin-car-form-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-2 gap-4">
          <div><label className="titan-label">Brand *</label><input className="titan-input" value={form.brand} onChange={(e) => update("brand", e.target.value)} required data-testid="admin-form-brand" /></div>
          <div><label className="titan-label">Model name *</label><input className="titan-input" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="admin-form-name" /></div>
          <div><label className="titan-label">Category *</label>
            <select className="titan-input" value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="admin-form-category">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><label className="titan-label">Daily rate within Accra (GH₵) *</label><input type="number" min="0" step="50" className="titan-input" value={form.daily_rate} onChange={(e) => update("daily_rate", e.target.value)} required data-testid="admin-form-rate" /></div>
          <div><label className="titan-label">Daily rate outside Accra (GH₵)</label><input type="number" min="0" step="50" className="titan-input" value={form.daily_rate_outside || ""} placeholder="(same as within if blank)" onChange={(e) => update("daily_rate_outside", e.target.value)} data-testid="admin-form-rate-outside" /></div>
          <div><label className="titan-label">Transmission</label>
            <select className="titan-input" value={form.transmission} onChange={(e) => update("transmission", e.target.value)}>
              <option value="automatic">Automatic</option><option value="manual">Manual</option>
            </select>
          </div>
          <div><label className="titan-label">Fuel</label>
            <select className="titan-input" value={form.fuel} onChange={(e) => update("fuel", e.target.value)}>
              <option value="petrol">Petrol</option><option value="diesel">Diesel</option><option value="hybrid">Hybrid</option><option value="electric">Electric</option>
            </select>
          </div>
          <div><label className="titan-label">Seats</label><input type="number" min="1" max="20" className="titan-input" value={form.seats} onChange={(e) => update("seats", e.target.value)} /></div>
          <div><label className="titan-label">Luggage</label><input type="number" min="0" max="20" className="titan-input" value={form.luggage} onChange={(e) => update("luggage", e.target.value)} /></div>
          <div className="col-span-2"><label className="titan-label">Image URL *</label><input className="titan-input" value={form.image_url} onChange={(e) => update("image_url", e.target.value)} required data-testid="admin-form-image" /></div>
          <div className="col-span-2"><label className="titan-label">Features (comma separated)</label><input className="titan-input" value={Array.isArray(form.features) ? form.features.join(", ") : form.features} onChange={(e) => update("features", e.target.value)} /></div>
          <div className="col-span-2"><label className="titan-label">Description</label><textarea rows={3} className="titan-input" value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="available" checked={form.available} onChange={(e) => update("available", e.target.checked)} />
            <label htmlFor="available" className="text-sm text-[#0d2040]">Available for booking</label>
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="admin-form-save-btn">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : car ? "Save changes" : "Create car"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
